// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReservationForm from '../../components/admin/ReservationForm.tsx';
import { RoomLayoutMap, RoomTableMap } from '../../components/admin/RoomMaps.tsx';
import RoomSeatPanel from '../../components/admin/RoomSeatPanel.tsx';
import DashboardTodayBoard from '../../components/admin/DashboardTodayBoard.tsx';
import DashboardModals from '../../components/admin/DashboardModals.tsx';
import DashboardSummaryPanel from '../../components/admin/DashboardSummaryPanel.tsx';
import RoomFloorTabs from '../../components/admin/RoomFloorTabs.tsx';
import RoomCapacityOverview from '../../components/admin/RoomCapacityOverview.tsx';
import { usesTableMap } from '../../utils/roomMode.ts';
import {
  extractReservationTableIds,
  extractMesaIdsFromOcupacoes,
  formatPeopleLabel,
  getPeopleCount,
} from '../../utils/reservationPeople.ts';
import {
  STATUS_LABELS,
  areStringArraysEqual,
  normalizeStatusValue,
  getReservationStatusMeta,
  normalizeDate,
  formatDateKey,
  shiftDate,
  formatDashboardDateLabel,
  sortReservationsByTime,
  buildReservedTableMeta,
  getTableCapacityRange,
  formatTableCapacityRange,
  getTableCapacityGroupKey,
  calculateCombinedTableCapacity,
  buildBelowMinimumCapacityWarning,
  isAwaitingGuestOcupacao,
  getReleaseAllActionLabel,
  buildTableTooltip,
} from '../../utils/adminDashboardHelpers.ts';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { useReservation } from '../../contexts/ReservationContext.tsx';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import {
  fetchMesaOcupacoes,
  ocuparMesa,
  selecionarMesa,
  liberarMesa,
  MesaOcupacao,
  fetchSalaOcupacoes,
  ocuparSala,
  liberarSalaOcupacao,
  SalaOcupacao,
  fetchRestaurantSettings,
  fetchWaitlistEntries,
  seatWaitlistEntry,
  WaitlistEntry
} from '../../services/api.ts';
import { createQrCodePngBlob, createQrCodeSvgDataUrl } from '../../utils/qrCode.ts';
import '../../styles/admin-dashboard.css';


interface NoShowConfirmationState {
  reservationId: string;
  reservationName: string;
  reservationTime: string;
}

interface QrCodeModalState {
  title: string;
  link: string;
  fileName: string;
}

type MesaSelectModalMode = 'seat' | 'select' | 'leave' | 'waitlist-seat';

interface MesaSelectModalState {
  reservaId?: string;
  waitlistEntry?: WaitlistEntry;
  guests: number;
  salaId: string | number;
  mode: MesaSelectModalMode;
}

interface RoomSeatModalState {
  source: 'reservation' | 'waitlist';
  sourceId: string;
  name: string;
  phone?: string | null;
  guests: number;
  salaId: string;
}

interface SalaReleaseModalState {
  ocupacao: SalaOcupacao;
  quantidadeSaida: number;
}

type TodayReservationTab = 'upcoming' | 'seated' | 'finalized';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { reservations, updateReservation, reload } = useReservation();
  const {
    floors,
    currentFloor,
    setCurrentFloor,
    updateTable,
    createDefaultFloor,
  } = useRestaurant();
  const [selectedDate, setSelectedDate] = useState<Date>(() => normalizeDate(new Date()));
  const [restaurantSlug, setRestaurantSlug] = useState('gourmex');
  const [mesaOcupacoes, setMesaOcupacoes] = useState<MesaOcupacao[]>([]);
  const [salaOcupacoes, setSalaOcupacoes] = useState<SalaOcupacao[]>([]);
  const [occupancyLoading, setOccupancyLoading] = useState(false);
  const [occupancyError, setOccupancyError] = useState('');
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [selectedReservationId, setSelectedReservationId] = useState('');
  const [seatCount, setSeatCount] = useState(1);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showMesaSelectModal, setShowMesaSelectModal] = useState<MesaSelectModalState | null>(null);
  const [roomSeatModal, setRoomSeatModal] = useState<RoomSeatModalState | null>(null);
  const [roomSeatSubmitting, setRoomSeatSubmitting] = useState(false);
  const [salaReleaseModal, setSalaReleaseModal] = useState<SalaReleaseModalState | null>(null);
  const [salaReleaseSubmitting, setSalaReleaseSubmitting] = useState(false);
  const [mesaSelectValues, setMesaSelectValues] = useState<string[]>([]);
  const [mesaModalSubmitting, setMesaModalSubmitting] = useState(false);
  const [mesaCapacityOverflowConfirmed, setMesaCapacityOverflowConfirmed] = useState(false);
  const [seatCapacityWarningConfirmed, setSeatCapacityWarningConfirmed] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [restaurantSettings, setRestaurantSettings] = useState<any>({});
  const [restaurantSettingsLoaded, setRestaurantSettingsLoaded] = useState(false);
  const [todayRoomFilter, setTodayRoomFilter] = useState<string>('all');
  const [todayConfirmedOnly, setTodayConfirmedOnly] = useState(false);
  const [todayReservationTab, setTodayReservationTab] = useState<TodayReservationTab>('upcoming');
  const [boardFilter, setBoardFilter] = useState<'both' | 'reservations' | 'waitlist'>('both');
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [waitlistRefreshKey, setWaitlistRefreshKey] = useState(0);
  const [showEditReservationModal, setShowEditReservationModal] = useState(false);
  const [editingReservation, setEditingReservation] = useState<any>(null);
  const [noShowConfirmation, setNoShowConfirmation] = useState<NoShowConfirmationState | null>(null);
  const [qrCodeModal, setQrCodeModal] = useState<QrCodeModalState | null>(null);
  const finalizedSelectionCleanupRef = useRef<Set<string>>(new Set());
  const selectedTableNameInputRef = useRef<HTMLInputElement | null>(null);
  const reloadReservations = useCallback(() => {
    if (typeof reload === 'function') {
      return reload();
    }
    return Promise.resolve();
  }, [reload]);
  const [isEditingSelectedTableName, setIsEditingSelectedTableName] = useState(false);
  const [selectedTableNameDraft, setSelectedTableNameDraft] = useState('');
  const [selectedTableNameSaving, setSelectedTableNameSaving] = useState(false);
  const [initialFloorCreating, setInitialFloorCreating] = useState(false);
  const selectedDateKey = useMemo(() => formatDateKey(selectedDate), [selectedDate]);
  const reservationsFeatureEnabled = restaurantSettings?.reservo_usar_reservas !== false;
  const waitlistFeatureEnabled = restaurantSettings?.reservo_usar_fila === true;
  const legacyMapDefault = restaurantSettings?.reservo_usar_mapa_mesas !== false;
  const currentFloorData = floors.find(floor => String(floor.id) === String(currentFloor));
  const usarMapaMesas = usesTableMap(currentFloorData, legacyMapDefault);

  // Não há mais busca/listagem de clientes, apenas cadastro novo

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await fetchRestaurantSettings();
        setRestaurantSettings(data || {});
        if (data?.checkin_slug) {
          setRestaurantSlug(data.checkin_slug);
        }
      } catch (error) {
        console.error('Erro ao carregar configurações do restaurante:', error);
      } finally {
        setRestaurantSettingsLoaded(true);
      }
    };
    loadSettings();
  }, []);

  const loadMesaOcupacoes = useCallback(async (silent = false) => {
    if (!restaurantSettingsLoaded) return;
    if (!silent) setOccupancyLoading(true);
    try {
      const [mesas, salas] = await Promise.all([
        fetchMesaOcupacoes({ ativas: true, data: selectedDateKey }),
        fetchSalaOcupacoes({ ativas: true, data: selectedDateKey }),
      ]);
      setMesaOcupacoes(mesas || []);
      setSalaOcupacoes(salas || []);
      setOccupancyError('');
    } catch (error) {
      if (!silent) {
        setMesaOcupacoes([]);
        setSalaOcupacoes([]);
        setOccupancyError('Não foi possível carregar as ocupações dos ambientes.');
      }
    } finally {
      if (!silent) setOccupancyLoading(false);
    }
  }, [restaurantSettingsLoaded, selectedDateKey]);

  useEffect(() => {
    loadMesaOcupacoes();
  }, [loadMesaOcupacoes]);

  // Mantém o contador da fila de espera sincronizado com o backend,
  // mesmo quando a coluna da fila não está visível.
  const loadWaitlistCount = useCallback(async () => {
    if (!waitlistFeatureEnabled) {
      setWaitlistCount(0);
      return;
    }

    try {
      const data = await fetchWaitlistEntries({ data: selectedDateKey, ativas: true });
      setWaitlistCount(Array.isArray(data) ? data.length : 0);
    } catch (error) {
      setWaitlistCount(0);
    }
  }, [selectedDateKey, waitlistFeatureEnabled]);

  useEffect(() => {
    loadWaitlistCount();
  }, [loadWaitlistCount]);

  useEffect(() => {
    const interval = setInterval(() => {
      void reloadReservations();
      void loadMesaOcupacoes(true);
      void loadWaitlistCount();
      if (waitlistFeatureEnabled) {
        setWaitlistRefreshKey((current) => current + 1);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [loadMesaOcupacoes, loadWaitlistCount, reloadReservations, waitlistFeatureEnabled]);

  useEffect(() => {
    if (!reservationsFeatureEnabled && waitlistFeatureEnabled) {
      setBoardFilter('waitlist');
      return;
    }

    if (reservationsFeatureEnabled && !waitlistFeatureEnabled) {
      setBoardFilter('reservations');
      return;
    }

    if (reservationsFeatureEnabled && waitlistFeatureEnabled) {
      setBoardFilter('both');
    }
  }, [reservationsFeatureEnabled, waitlistFeatureEnabled]);

  const handleCreateInitialFloor = useCallback(async () => {
    if (initialFloorCreating) return;

    if (floors.length > 0) {
      navigate('/reservo/plantas');
      return;
    }

    setInitialFloorCreating(true);
    let shouldResetCreating = true;

    try {
      await createDefaultFloor();
      shouldResetCreating = false;
      navigate('/reservo/plantas', {
        state: { initialReservoSetup: true },
      });
    } catch (error) {
      console.error('Erro ao criar ambiente inicial do Reservo:', error);
      toast.error('Não foi possível criar o ambiente inicial.');
    } finally {
      if (shouldResetCreating) {
        setInitialFloorCreating(false);
      }
    }
  }, [createDefaultFloor, floors.length, initialFloorCreating, navigate]);

  useEffect(() => {
    setSelectedTableId(null);
    setSelectedReservationId('');
    setActionError('');
  }, [currentFloor, selectedDate]);

  useEffect(() => {
    setTodayReservationTab('upcoming');
  }, [selectedDateKey]);

  useEffect(() => {
    if (!restaurantSettings?.reservo_confirmacao_cliente) {
      setTodayConfirmedOnly(false);
    }
  }, [restaurantSettings?.reservo_confirmacao_cliente]);

  const normalize = (res: any) => {
    const tableIds = extractReservationTableIds(res);
    const primaryTableId = tableIds[0] ?? (res.mesa_id ?? null);
    let tableName = primaryTableId ? `Mesa ${primaryTableId}` : null;
    let roomName = `Sala ${res.sala_id}`;

    if (floors && floors.length > 0) {
      const tablesById = new Map(
        floors
          .flatMap((floor) => floor.tables || [])
          .map((table: any) => [String(table.id), table])
      );
      const tableNames = tableIds
        .map((tableId) => tablesById.get(String(tableId)))
        .filter(Boolean)
        .map((table: any) => table.name || `Mesa ${table.id}`);
      const room = floors.find(floor => floor.id === res.sala_id?.toString());

      if (tableNames.length > 0) {
        tableName = tableNames.join(', ');
      }
      if (room) roomName = room.name;
    }

    // Parse date string ignoring time (format: YYYY-MM-DD) - same as calendar
    const [year, month, day] = res.data.split('-').map(Number);
    const resDate = new Date(year, month - 1, day);

    return {
      id: res.id,
      date: resDate,
      time: res.hora,
      name: res.nome_cliente,
      tableId: primaryTableId,
      tableIds,
      roomId: res.sala_id,
      tableName,
      roomName,
      guests: res.numero_pessoas,
      phone: res.telefone_cliente,
      status: res.status,
      statusCliente: res.status_cliente,
      notes: res.obs
    };
  };

  const normalized = reservations.map(normalize);
  const reservationById = useMemo(() => {
    const map = new Map<string, any>();
    normalized.forEach((reservation) => {
      if (reservation?.id === null || reservation?.id === undefined) return;
      map.set(String(reservation.id), reservation);
    });
    return map;
  }, [normalized]);
  const floorsById = useMemo(() => {
    return new Map(
      (floors || []).map((floor) => [String(floor.id), floor])
    );
  }, [floors]);
  const tableNameById = useMemo(() => {
    const map = new Map<string, string>();
    (floors || []).forEach((floor) => {
      (floor?.tables || []).forEach((table: any) => {
        if (table?.id === null || table?.id === undefined) return;
        map.set(String(table.id), table.name || `Mesa ${table.id}`);
      });
    });
    return map;
  }, [floors]);
  const resolveTableDisplayNames = useCallback((tableIds: Array<string | number | null | undefined>) => {
    const labels = [...new Set(
      (Array.isArray(tableIds) ? tableIds : [])
        .map((tableId) => (
          tableId === null || tableId === undefined
            ? null
            : (tableNameById.get(String(tableId)) || `Mesa ${tableId}`)
        ))
        .filter(Boolean) as string[]
    )];

    return labels.join(', ');
  }, [tableNameById]);
  const currentFloorHasLayout = usarMapaMesas && currentFloorData?.modo_layout !== false;
  const requiresRestaurantConfirmation = Boolean(restaurantSettings?.reservo_confirmacao_restaurante);
  const requiresClientConfirmation = Boolean(restaurantSettings?.reservo_confirmacao_cliente);
  const isFinalizedReservationStatus = useCallback((status?: string | null) => {
    const { displayStatus } = getReservationStatusMeta(status, requiresRestaurantConfirmation);
    return displayStatus === 'concluida' || displayStatus === 'no_show';
  }, [requiresRestaurantConfirmation]);
  const isUnavailableReservationStatus = useCallback((status?: string | null) => {
    const { displayStatus } = getReservationStatusMeta(status, requiresRestaurantConfirmation);
    return displayStatus === 'cancelada' || displayStatus === 'concluida' || displayStatus === 'no_show';
  }, [requiresRestaurantConfirmation]);
  const shouldHideFromTodayReservations = useCallback((status?: string | null) => {
    const { displayStatus } = getReservationStatusMeta(status, requiresRestaurantConfirmation);
    return displayStatus === 'cancelada';
  }, [requiresRestaurantConfirmation]);
  const isReservationCustomerConfirmed = useCallback((status?: string | null) => {
    return normalizeStatusValue(status) === 'confirmada';
  }, []);
  const modalFloorData = useMemo(() => {
    if (!showMesaSelectModal?.salaId) return null;
    return floorsById.get(String(showMesaSelectModal.salaId)) || null;
  }, [floorsById, showMesaSelectModal]);

  const selectedDateLabel = useMemo(
    () => formatDashboardDateLabel(selectedDate),
    [selectedDate]
  );
  const isSameDay = (dateA: Date, dateB: Date) =>
    dateA.getDate() === dateB.getDate()
    && dateA.getMonth() === dateB.getMonth()
    && dateA.getFullYear() === dateB.getFullYear();

  const unavailableTableIds = useMemo(() => {
    return new Set(
      (mesaOcupacoes || [])
        .map((ocupacao) => ocupacao?.mesa_id)
        .filter((mesaId) => mesaId !== null && mesaId !== undefined)
        .map((mesaId) => String(mesaId))
    );
  }, [mesaOcupacoes]);

  const waitingTableIds = useMemo(() => {
    return new Set(
      (mesaOcupacoes || [])
        .filter((ocupacao) => isAwaitingGuestOcupacao(ocupacao))
        .map((ocupacao) => ocupacao?.mesa_id)
        .filter((mesaId) => mesaId !== null && mesaId !== undefined)
        .map((mesaId) => String(mesaId))
    );
  }, [mesaOcupacoes]);

  const occupiedTableIds = useMemo(() => {
    return new Set(
      (mesaOcupacoes || [])
        .filter((ocupacao) => !isAwaitingGuestOcupacao(ocupacao))
        .map((ocupacao) => ocupacao?.mesa_id)
        .filter((mesaId) => mesaId !== null && mesaId !== undefined)
        .map((mesaId) => String(mesaId))
    );
  }, [mesaOcupacoes]);

  const ocupacaoByMesaId = useMemo(() => {
    const map = new Map<string, MesaOcupacao>();
    (mesaOcupacoes || []).forEach((ocupacao) => {
      if (ocupacao?.mesa_id === null || ocupacao?.mesa_id === undefined) return;
      map.set(String(ocupacao.mesa_id), ocupacao);
    });
    return map;
  }, [mesaOcupacoes]);
  const awaitingOcupacoesByReservationId = useMemo(() => {
    const map = new Map<string, MesaOcupacao[]>();
    (mesaOcupacoes || []).forEach((ocupacao) => {
      if (ocupacao?.reserva_id === null || ocupacao?.reserva_id === undefined) return;
      if (!isAwaitingGuestOcupacao(ocupacao)) return;
      const key = String(ocupacao.reserva_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key)?.push(ocupacao);
    });
    return map;
  }, [mesaOcupacoes]);

  const seatedOcupacoesByReservationId = useMemo(() => {
    const map = new Map<string, MesaOcupacao[]>();
    (mesaOcupacoes || []).forEach((ocupacao) => {
      if (ocupacao?.reserva_id === null || ocupacao?.reserva_id === undefined) return;
      if (isAwaitingGuestOcupacao(ocupacao)) return;
      const key = String(ocupacao.reserva_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key)?.push(ocupacao);
    });
    return map;
  }, [mesaOcupacoes]);
  const getReservationOcupacoes = useCallback((
    reservationId?: string | number | null,
    options?: { awaitingOnly?: boolean; seatedOnly?: boolean }
  ) => {
    if (!reservationId) return [] as MesaOcupacao[];
    const awaiting = awaitingOcupacoesByReservationId.get(String(reservationId)) || [];
    const seated = seatedOcupacoesByReservationId.get(String(reservationId)) || [];

    if (options?.awaitingOnly) return awaiting;
    if (options?.seatedOnly) return seated;
    return [...awaiting, ...seated];
  }, [awaitingOcupacoesByReservationId, seatedOcupacoesByReservationId]);

  const canUseTableForReservation = useCallback((
    tableId?: string | number | null,
    reservationId?: string | number | null,
  ) => {
    if (tableId === null || tableId === undefined) return false;
    const normalizedTableId = String(tableId);
    if (!unavailableTableIds.has(normalizedTableId)) return true;
    if (reservationId === null || reservationId === undefined) return false;

    const ocupacao = ocupacaoByMesaId.get(normalizedTableId);
    if (!ocupacao) return false;

    return (
      isAwaitingGuestOcupacao(ocupacao)
      && String(ocupacao.reserva_id) === String(reservationId)
    );
  }, [ocupacaoByMesaId, unavailableTableIds]);

  const tableTooltipById = useMemo(() => {
    const map = new Map<string, string>();
    (currentFloorData?.tables || []).forEach((table: any) => {
      const tableId = String(table.id);
      const ocupacao = ocupacaoByMesaId.get(tableId);
      const reservation = ocupacao?.reserva_id !== null && ocupacao?.reserva_id !== undefined
        ? reservationById.get(String(ocupacao.reserva_id)) || null
        : null;
      map.set(tableId, buildTableTooltip(table, ocupacao, reservation));
    });
    return map;
  }, [currentFloorData, ocupacaoByMesaId, reservationById]);

  const floorStats = useMemo(() => {
    const occupiedByFloor: Record<string, number> = {};
    (mesaOcupacoes || []).forEach((ocupacao) => {
      if (ocupacao?.sala_id === null || ocupacao?.sala_id === undefined) return;
      if (isAwaitingGuestOcupacao(ocupacao)) return;
      const floor = floorsById.get(String(ocupacao.sala_id));
      if (!floor || !usesTableMap(floor, legacyMapDefault)) return;
      const key = String(ocupacao.sala_id);
      occupiedByFloor[key] = (occupiedByFloor[key] || 0) + 1;
    });
    (salaOcupacoes || []).forEach((ocupacao) => {
      if (ocupacao?.sala_id === null || ocupacao?.sala_id === undefined) return;
      const floor = floorsById.get(String(ocupacao.sala_id));
      if (!floor || usesTableMap(floor, legacyMapDefault)) return;
      const key = String(ocupacao.sala_id);
      occupiedByFloor[key] = (occupiedByFloor[key] || 0)
        + Math.max(0, Number(ocupacao.numero_pessoas_presentes) || 0);
    });

    const stats = new Map<string, { total: number; occupied: number; available: number }>();
    floors.forEach((floor) => {
      const total = usesTableMap(floor, legacyMapDefault)
        ? (floor.tables?.length || 0)
        : Math.max(0, Number(floor.capacidade_maxima) || 0);
      const occupied = occupiedByFloor[String(floor.id)] || 0;
      stats.set(String(floor.id), {
        total,
        occupied,
        available: Math.max(0, total - occupied)
      });
    });
    return stats;
  }, [floors, floorsById, mesaOcupacoes, salaOcupacoes, legacyMapDefault]);

  const seatedReservationIds = useMemo(() => {
    const seatedOccupancies = [
      ...salaOcupacoes.filter((ocupacao) => Number(ocupacao.numero_pessoas_presentes) > 0),
      ...mesaOcupacoes.filter((ocupacao) => !isAwaitingGuestOcupacao(ocupacao)),
    ];
    return new Set(seatedOccupancies
      .map((ocupacao) => ocupacao?.reserva_id)
      .filter((reservaId) => reservaId !== null && reservaId !== undefined)
      .map((reservaId) => String(reservaId)));
  }, [mesaOcupacoes, salaOcupacoes]);
  const awaitingReservationIds = useMemo(() => (
    new Set(Array.from(awaitingOcupacoesByReservationId.keys()))
  ), [awaitingOcupacoesByReservationId]);
  const salaOcupacaoByReservationId = useMemo(() => {
    const map = new Map<string, SalaOcupacao>();
    salaOcupacoes.forEach((ocupacao) => {
      if (
        ocupacao.reserva_id === null
        || ocupacao.reserva_id === undefined
        || Number(ocupacao.numero_pessoas_presentes) <= 0
      ) {
        return;
      }
      map.set(String(ocupacao.reserva_id), ocupacao);
    });
    return map;
  }, [salaOcupacoes]);
  const currentFloorSalaOcupacoes = useMemo(() => (
    salaOcupacoes
      .filter((ocupacao) => (
        String(ocupacao.sala_id) === String(currentFloorData?.id)
        && Number(ocupacao.numero_pessoas_presentes) > 0
      ))
      .sort((first, second) => (
        new Date(first.seated_at).getTime() - new Date(second.seated_at).getTime()
      ))
  ), [currentFloorData?.id, salaOcupacoes]);
  const finalizedSelectedMesaIds = useMemo(() => {
    return (mesaOcupacoes || [])
      .filter((ocupacao) => isAwaitingGuestOcupacao(ocupacao))
      .filter((ocupacao) => {
        if (ocupacao?.reserva_id === null || ocupacao?.reserva_id === undefined) return false;
        const reservation = reservationById.get(String(ocupacao.reserva_id));
        if (!reservation) return false;
        return isUnavailableReservationStatus(reservation.status);
      })
      .map((ocupacao) => String(ocupacao.mesa_id));
  }, [isUnavailableReservationStatus, mesaOcupacoes, reservationById]);

  useEffect(() => {
    const mesaIdsToRelease = finalizedSelectedMesaIds.filter(
      (mesaId) => !finalizedSelectionCleanupRef.current.has(mesaId)
    );
    if (mesaIdsToRelease.length === 0) return;

    mesaIdsToRelease.forEach((mesaId) => finalizedSelectionCleanupRef.current.add(mesaId));
    let cancelled = false;

    const releaseFinalizedSelections = async () => {
      let releasedAny = false;

      for (const mesaId of mesaIdsToRelease) {
        try {
          await liberarMesa(mesaId, { data: selectedDateKey });
          releasedAny = true;
        } catch (error) {
          console.error('Erro ao liberar mesa selecionada de reserva finalizada:', error);
        } finally {
          finalizedSelectionCleanupRef.current.delete(mesaId);
        }
      }

      if (!cancelled && releasedAny) {
        await loadMesaOcupacoes();
      }
    };

    releaseFinalizedSelections();

    return () => {
      cancelled = true;
    };
  }, [finalizedSelectedMesaIds, loadMesaOcupacoes, selectedDateKey]);

  const todayReservationsForFloor = useMemo(() => {
    if (!currentFloorData) return [];
    return normalized.filter((res) => {
      if (!res?.date || !isSameDay(res.date, selectedDate)) return false;
      if (shouldHideFromTodayReservations(res.status)) return false;
      return String(res.roomId) === String(currentFloorData.id);
    });
  }, [normalized, currentFloorData, selectedDate, shouldHideFromTodayReservations]);

  const todayReservationsAll = useMemo(() => {
    const base = normalized.filter((res) => (
      res?.date
      && isSameDay(res.date, selectedDate)
      && !shouldHideFromTodayReservations(res.status)
    ));
    return base;
  }, [normalized, selectedDate, shouldHideFromTodayReservations]);

  const filteredTodayReservations = useMemo(() => {
    const reservationsByRoom = todayRoomFilter === 'all'
      ? todayReservationsAll
      : todayReservationsAll.filter((res) => String(res.roomId) === String(todayRoomFilter));

    if (!requiresClientConfirmation || !todayConfirmedOnly) {
      return reservationsByRoom;
    }

    return reservationsByRoom.filter((res) => isReservationCustomerConfirmed(res.statusCliente));
  }, [
    isReservationCustomerConfirmed,
    requiresClientConfirmation,
    todayConfirmedOnly,
    todayReservationsAll,
    todayRoomFilter
  ]);

  const dashboardSummary = useMemo(() => {
    const stats = floorStats.get(String(currentFloorData?.id));
    const occupied = stats?.occupied || 0;
    const available = stats?.available || 0;
    const selectedTables = waitingTableIds.size;
    const reservationsToday = todayReservationsAll.length;

    return {
      selectedTables,
      occupied,
      available,
      reservationsToday
    };
  }, [
    currentFloorData?.id,
    floorStats,
    todayReservationsAll,
    waitingTableIds
  ]);

  const effectiveBoardFilter = useMemo(() => {
    if (reservationsFeatureEnabled && waitlistFeatureEnabled) return boardFilter;
    if (waitlistFeatureEnabled) return 'waitlist';
    return 'reservations';
  }, [boardFilter, reservationsFeatureEnabled, waitlistFeatureEnabled]);

  const dashboardWaitingCount = dashboardSummary.selectedTables + (waitlistFeatureEnabled ? waitlistCount : 0);

  const reservationLink = useMemo(() => {
    const baseUrl = typeof window !== 'undefined'
      ? String(window.location.origin).replace(/\/+$/, '')
      : '';
    return `${baseUrl}/reservar/${restaurantSlug}`;
  }, [restaurantSlug]);

  const handleCopyReservationLink = useCallback(async () => {
    if (!reservationLink) return;

    try {
      await navigator.clipboard.writeText(reservationLink);
      toast.success('Link de reservas copiado.');
    } catch (error) {
      toast.error('Não foi possível copiar o link de reservas.');
    }
  }, [reservationLink]);

  const waitlistLink = useMemo(() => {
    const baseUrl = typeof window !== 'undefined'
      ? String(window.location.origin).replace(/\/+$/, '')
      : '';
    return `${baseUrl}/fila/${restaurantSlug}`;
  }, [restaurantSlug]);

  const handleCopyWaitlistLink = useCallback(async () => {
    if (!waitlistLink) return;

    try {
      await navigator.clipboard.writeText(waitlistLink);
      toast.success('Link da fila de espera copiado.');
    } catch (error) {
      toast.error('Não foi possível copiar o link da fila de espera.');
    }
  }, [waitlistLink]);

  const handleOpenQrCodeModal = useCallback((modalState: QrCodeModalState) => {
    if (!modalState.link) return;

    try {
      createQrCodeSvgDataUrl(modalState.link);
      setQrCodeModal(modalState);
    } catch (error) {
      toast.error('Não foi possível gerar o QR Code para este link.');
    }
  }, []);

  const handleCopyQrCode = useCallback(async () => {
    if (!qrCodeModal?.link) return;

    try {
      const blob = await createQrCodePngBlob(qrCodeModal.link);
      const ClipboardItemConstructor = typeof window !== 'undefined'
        ? (window as any).ClipboardItem
        : null;
      const clipboard = navigator.clipboard as any;

      if (!ClipboardItemConstructor || !clipboard?.write) {
        await navigator.clipboard.writeText(qrCodeModal.link);
        toast.info('Seu navegador não permite copiar imagem. Link copiado.');
        return;
      }

      await clipboard.write([
        new ClipboardItemConstructor({ 'image/png': blob }),
      ]);
      toast.success('QR Code copiado.');
    } catch (error) {
      toast.error('Não foi possível copiar o QR Code.');
    }
  }, [qrCodeModal]);

  const handleSaveQrCode = useCallback(async () => {
    if (!qrCodeModal?.link || typeof document === 'undefined') return;

    try {
      const blob = await createQrCodePngBlob(qrCodeModal.link);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = qrCodeModal.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success('QR Code salvo.');
    } catch (error) {
      toast.error('Não foi possível salvar o QR Code.');
    }
  }, [qrCodeModal]);

  const activeQrCodeDataUrl = useMemo(() => {
    if (!qrCodeModal?.link) return '';

    try {
      return createQrCodeSvgDataUrl(qrCodeModal.link);
    } catch (error) {
      return '';
    }
  }, [qrCodeModal]);

  const availableReservationsForFloor = useMemo(() => {
    return todayReservationsForFloor.filter((res) => (
      !isUnavailableReservationStatus(res.status)
    ));
  }, [todayReservationsForFloor, isUnavailableReservationStatus]);

  const availableReservationsByRoom = useMemo(() => {
    const map = new Map<string, any[]>();
    todayReservationsAll.forEach((res) => {
      if (isUnavailableReservationStatus(res?.status)) return;
      const roomKey = String(res?.roomId || '');
      if (!roomKey) return;
      if (!map.has(roomKey)) map.set(roomKey, []);
      map.get(roomKey)?.push(res);
    });

    for (const [roomId, list] of map.entries()) {
      map.set(roomId, sortReservationsByTime(list));
    }

    return map;
  }, [todayReservationsAll, isUnavailableReservationStatus]);

  const reservedTableMetaForFloor = useMemo(() => {
    return buildReservedTableMeta(availableReservationsForFloor, selectedReservationId || null);
  }, [availableReservationsForFloor, selectedReservationId]);

  const selectedReservation = useMemo(() => {
    if (!selectedReservationId) return null;
    return availableReservationsForFloor.find(
      (res) => String(res.id) === String(selectedReservationId)
    ) || null;
  }, [availableReservationsForFloor, selectedReservationId]);

  const selectedTable = useMemo(() => {
    if (!currentFloorData || !selectedTableId) return null;
    return currentFloorData.tables.find((table) => String(table.id) === String(selectedTableId)) || null;
  }, [currentFloorData, selectedTableId]);
  useEffect(() => {
    setIsEditingSelectedTableName(false);
    setSelectedTableNameSaving(false);
    setSelectedTableNameDraft(selectedTable?.name || '');
  }, [selectedTable?.id, selectedTable?.name]);
  useEffect(() => {
    if (!isEditingSelectedTableName || !selectedTableNameInputRef.current) return;
    selectedTableNameInputRef.current.focus();
    selectedTableNameInputRef.current.select();
  }, [isEditingSelectedTableName]);

  const selectedTablePendingReservations = useMemo(() => {
    if (!selectedTable?.id) return [];
    return sortReservationsByTime(
      availableReservationsForFloor.filter((res) => (
        extractReservationTableIds(res).some((tableId) => String(tableId) === String(selectedTable.id))
      ))
    );
  }, [availableReservationsForFloor, selectedTable]);

  const selectedTableCapacity = useMemo(() => {
    if (!selectedTable) {
      return {
        min: 1,
        max: 1,
        label: ''
      };
    }

    const min = Math.max(1, Math.floor(Number(selectedTable.minCovers) || 1));
    const max = Math.max(min, Math.floor(Number(selectedTable.maxCovers) || min));

    return {
      min,
      max,
      label: min === max ? formatPeopleLabel(max) : `${min} a ${formatPeopleLabel(max)}`
    };
  }, [selectedTable]);

  const selectedTableOcupacao = useMemo(() => {
    if (!selectedTable) return null;
    return ocupacaoByMesaId.get(String(selectedTable.id)) || null;
  }, [ocupacaoByMesaId, selectedTable]);

  const selectedTableReservation = useMemo(() => {
    if (selectedTableOcupacao?.reserva_id === null || selectedTableOcupacao?.reserva_id === undefined) {
      return null;
    }
    return reservationById.get(String(selectedTableOcupacao.reserva_id)) || null;
  }, [reservationById, selectedTableOcupacao]);
  const selectedTableReservationActiveOcupacoes = useMemo(() => {
    if (!selectedTableReservation?.id) return [];
    return getReservationOcupacoes(selectedTableReservation.id);
  }, [getReservationOcupacoes, selectedTableReservation]);
  const selectedTableReservationActiveTableIds = useMemo(() => (
    extractMesaIdsFromOcupacoes(selectedTableReservationActiveOcupacoes)
  ), [selectedTableReservationActiveOcupacoes]);
  const selectedTableReservationActiveTableNames = useMemo(() => (
    resolveTableDisplayNames(selectedTableReservationActiveTableIds)
  ), [resolveTableDisplayNames, selectedTableReservationActiveTableIds]);
  const selectedTableCanLeaveAll = selectedTableReservationActiveTableIds.length > 1;
  const selectedTableLeaveAllLabel = getReleaseAllActionLabel(selectedTableReservationActiveOcupacoes);

  const selectedTableIsAwaitingGuest = useMemo(() => {
    return isAwaitingGuestOcupacao(selectedTableOcupacao);
  }, [selectedTableOcupacao]);

  const selectedTableGuestCount = useMemo(() => {
    if (!selectedTableOcupacao) return null;
    return getPeopleCount(selectedTableOcupacao.numero_pessoas) || getPeopleCount(selectedTableReservation?.guests);
  }, [selectedTableOcupacao, selectedTableReservation]);
  const seatCapacityWarning = useMemo(() => {
    if (!selectedTable) return null;

    const currentGuestCount = selectedTableIsAwaitingGuest
      ? selectedTableGuestCount
      : getPeopleCount(seatCount);
    if (!currentGuestCount) return null;

    const { min } = getTableCapacityRange(selectedTable);
    if (currentGuestCount >= min) return null;

    return buildBelowMinimumCapacityWarning(
      currentGuestCount,
      min,
      `a mesa ${selectedTable.name || 'selecionada'}`
    );
  }, [seatCount, selectedTable, selectedTableGuestCount, selectedTableIsAwaitingGuest]);

  const selectedTableStatusMeta = useMemo(() => {
    if (selectedTableIsAwaitingGuest) {
      return {
        className: 'is-waiting',
        label: 'Aguardando cliente',
        statLabel: 'Situação',
        statValue: 'Esperando cliente',
        statDetail: selectedTableGuestCount
          ? `Reserva para ${formatPeopleLabel(selectedTableGuestCount)}`
          : (
            selectedTableReservationActiveTableIds.length > 1
              ? `${selectedTableReservationActiveTableIds.length} mesas vinculadas`
              : ''
          ),
        sectionTitle: 'Cliente aguardado',
        sectionDescription: selectedTableReservation?.time
          ? `Reserva às ${String(selectedTableReservation.time).slice(0, 5)} já vinculada a esta mesa`
          : 'Mesa sinalizada para uma reserva aguardando chegada',
        actionLabel: 'Liberar mesa',
      };
    }

    if (selectedTableOcupacao) {
      return {
        className: 'is-occupied',
        label: 'Ocupada',
        statLabel: 'Ocupação atual',
        statValue: selectedTableGuestCount ? formatPeopleLabel(selectedTableGuestCount) : 'Sem contagem',
        statDetail: selectedTableReservationActiveTableIds.length > 1
          ? `${selectedTableReservationActiveTableIds.length} mesas em uso`
          : '',
        sectionTitle: 'Atendimento atual',
        sectionDescription: selectedTableReservation?.time
          ? `Reserva às ${String(selectedTableReservation.time).slice(0, 5)}`
          : 'Mesa ocupada sem vínculo de reserva',
        actionLabel: 'Levantar',
      };
    }

    return {
      className: 'is-available',
      label: 'Livre',
      statLabel: 'Situação',
      statValue: 'Aguardando clientes',
      statDetail: '',
      sectionTitle: '',
      sectionDescription: '',
      actionLabel: '',
    };
  }, [
    selectedTableGuestCount,
    selectedTableIsAwaitingGuest,
    selectedTableOcupacao,
    selectedTableReservation,
    selectedTableReservationActiveTableIds.length
  ]);

  const modalReservationsForRoom = useMemo(() => {
    if (!showMesaSelectModal?.salaId) return [];
    return availableReservationsByRoom.get(String(showMesaSelectModal.salaId)) || [];
  }, [availableReservationsByRoom, showMesaSelectModal]);
  const modalReservation = useMemo(() => {
    if (!showMesaSelectModal?.reservaId) return null;
    return reservationById.get(String(showMesaSelectModal.reservaId)) || null;
  }, [reservationById, showMesaSelectModal]);
  const modalReservationLinkedTableIds = useMemo(() => (
    extractReservationTableIds(modalReservation)
  ), [modalReservation]);
  const modalReservationAwaitingTableIds = useMemo(() => (
    extractMesaIdsFromOcupacoes(
      getReservationOcupacoes(showMesaSelectModal?.reservaId, { awaitingOnly: true })
    )
  ), [getReservationOcupacoes, showMesaSelectModal]);
  const modalReservationSeatedTableIds = useMemo(() => (
    extractMesaIdsFromOcupacoes(
      getReservationOcupacoes(showMesaSelectModal?.reservaId, { seatedOnly: true })
    )
  ), [getReservationOcupacoes, showMesaSelectModal]);
  const mesaSelectModalIsSeatMode = showMesaSelectModal?.mode === 'seat';
  const mesaSelectModalIsLeaveMode = showMesaSelectModal?.mode === 'leave';
  const mesaSelectModalIsWaitlistSeatMode = showMesaSelectModal?.mode === 'waitlist-seat';
  const mesaSelectModalAllowsMultiple = !mesaSelectModalIsLeaveMode;
  const modalWaitlistEntry = showMesaSelectModal?.waitlistEntry || null;
  const modalSelectionSummary = mesaSelectModalAllowsMultiple
    ? (
      mesaSelectValues.length === 0
        ? 'Nenhuma mesa selecionada.'
        : `${mesaSelectValues.length} mesa${mesaSelectValues.length > 1 ? 's' : ''} selecionada${mesaSelectValues.length > 1 ? 's' : ''}.`
    )
    : '';

  const modalReservedTableMeta = useMemo(() => {
    return buildReservedTableMeta(modalReservationsForRoom, showMesaSelectModal?.reservaId || null);
  }, [modalReservationsForRoom, showMesaSelectModal]);

  const modalTableOptions = useMemo(() => {
    if (!modalFloorData?.tables) return [];

    const options = modalFloorData.tables
      .filter((table: any) => {
        if (mesaSelectModalIsLeaveMode) {
          return modalReservationSeatedTableIds.includes(String(table.id));
        }
        return canUseTableForReservation(table.id, showMesaSelectModal?.reservaId);
      })
      .map((table: any) => {
        const meta = modalReservedTableMeta.get(String(table.id));
        return {
          table,
          reservedCount: Number(meta?.count || 0),
          reservedForSelected: Boolean(meta?.hasSelectedReservation),
          isAwaitingForSelected: modalReservationAwaitingTableIds.includes(String(table.id)),
          isCurrentlySeated: modalReservationSeatedTableIds.includes(String(table.id)),
        };
      });

    return options.sort((a, b) => {
      if (a.isAwaitingForSelected !== b.isAwaitingForSelected) {
        return a.isAwaitingForSelected ? -1 : 1;
      }
      if (a.isCurrentlySeated !== b.isCurrentlySeated) {
        return a.isCurrentlySeated ? -1 : 1;
      }
      if (a.reservedForSelected !== b.reservedForSelected) {
        return a.reservedForSelected ? -1 : 1;
      }
      if (a.reservedCount !== b.reservedCount) {
        return b.reservedCount - a.reservedCount;
      }
      return String(a.table?.name || '').localeCompare(String(b.table?.name || ''));
    });
  }, [
    canUseTableForReservation,
    modalFloorData,
    modalReservationAwaitingTableIds,
    mesaSelectModalIsLeaveMode,
    modalReservationSeatedTableIds,
    modalReservedTableMeta,
    showMesaSelectModal
  ]);
  const modalTableGroups = useMemo(() => {
    if (!mesaSelectModalAllowsMultiple) return [];

    const groups = new Map<string, {
      key: string;
      label: string;
      min: number;
      max: number;
      options: any[];
    }>();

    modalTableOptions.forEach((option) => {
      const capacityRange = getTableCapacityRange(option.table);
      const key = getTableCapacityGroupKey(option.table);
      const currentGroup = groups.get(key) || {
        key,
        label: formatTableCapacityRange(option.table),
        min: capacityRange.min,
        max: capacityRange.max,
        options: [],
      };

      currentGroup.options.push(option);
      groups.set(key, currentGroup);
    });

    return Array.from(groups.values()).sort((a, b) => {
      const aHasSelectedTables = a.options.some(
        (option) => option.isAwaitingForSelected || option.reservedForSelected
      );
      const bHasSelectedTables = b.options.some(
        (option) => option.isAwaitingForSelected || option.reservedForSelected
      );
      if (aHasSelectedTables !== bHasSelectedTables) {
        return aHasSelectedTables ? -1 : 1;
      }
      if (a.min === b.min) return a.max - b.max;
      return a.min - b.min;
    });
  }, [mesaSelectModalAllowsMultiple, modalTableOptions]);
  const modalSelectedTableOptions = useMemo(() => (
    modalTableOptions.filter((option) => mesaSelectValues.includes(String(option.table.id)))
  ), [mesaSelectValues, modalTableOptions]);
  const modalSelectedTables = useMemo(() => (
    modalSelectedTableOptions.map((option) => option.table)
  ), [modalSelectedTableOptions]);
  const modalGuestCount = getPeopleCount(showMesaSelectModal?.guests ?? modalReservation?.guests) || 1;
  const modalSelectedCapacitySummary = useMemo(() => {
    if (modalSelectedTables.length === 0) {
      return {
        label: mesaSelectModalAllowsMultiple ? 'Capacidade selecionada' : 'Capacidade da mesa',
        value: 'Nenhuma mesa',
        detail: mesaSelectModalAllowsMultiple
          ? 'Selecione uma ou mais mesas para calcular a capacidade.'
          : 'Selecione a mesa que deseja liberar.',
        recommendedCapacity: null as number | null,
      };
    }

    if (modalSelectedTables.length === 1) {
      const selectedTable = modalSelectedTables[0];
      const { max } = getTableCapacityRange(selectedTable);
      return {
        label: 'Capacidade da mesa',
        value: formatTableCapacityRange(selectedTable),
        detail: `Referência desta mesa: ${formatPeopleLabel(max)}.`,
        recommendedCapacity: max,
      };
    }

    const recommendedCapacity = calculateCombinedTableCapacity(modalSelectedTables);

    return {
      label: 'Capacidade combinada',
      value: recommendedCapacity ? formatPeopleLabel(recommendedCapacity) : 'Não definida',
      recommendedCapacity,
    };
  }, [mesaSelectModalAllowsMultiple, modalSelectedTables]);
  const modalCurrentReservationMesaIds = useMemo(() => (
    [...new Set([
      ...modalReservationLinkedTableIds,
      ...modalReservationAwaitingTableIds,
      ...modalReservationSeatedTableIds,
    ])]
  ), [
    modalReservationAwaitingTableIds,
    modalReservationLinkedTableIds,
    modalReservationSeatedTableIds
  ]);
  const modalCapacityEvaluationTableIds = useMemo(() => {
    if (mesaSelectModalIsLeaveMode) {
      return modalCurrentReservationMesaIds.filter((tableId) => !mesaSelectValues.includes(String(tableId)));
    }

    if (mesaSelectModalIsSeatMode) {
      return [...new Set([...modalCurrentReservationMesaIds, ...mesaSelectValues])];
    }

    return [...new Set([...modalReservationSeatedTableIds, ...mesaSelectValues])];
  }, [
    mesaSelectModalIsLeaveMode,
    mesaSelectModalIsSeatMode,
    mesaSelectValues,
    modalCurrentReservationMesaIds,
    modalReservationSeatedTableIds
  ]);
  const modalCapacityEvaluationTables = useMemo(() => {
    if (!modalFloorData?.tables) return [];

    const tableMap = new Map(
      modalFloorData.tables.map((table: any) => [String(table.id), table])
    );

    return modalCapacityEvaluationTableIds
      .map((tableId) => tableMap.get(String(tableId)))
      .filter(Boolean);
  }, [modalCapacityEvaluationTableIds, modalFloorData]);
  const modalCapacityWarning = useMemo(() => {
    if (modalCapacityEvaluationTables.length === 0) {
      return null;
    }

    if (modalCapacityEvaluationTables.length === 1) {
      const [selectedTable] = modalCapacityEvaluationTables;
      const { min } = getTableCapacityRange(selectedTable);
      if (modalGuestCount < min) {
        return buildBelowMinimumCapacityWarning(
          modalGuestCount,
          min,
          `a mesa ${selectedTable.name || 'selecionada'}`
        );
      }
      return null;
    }

    const recommendedCapacity = calculateCombinedTableCapacity(modalCapacityEvaluationTables);
    if (recommendedCapacity !== null && modalGuestCount > recommendedCapacity) {
      return {
        title: 'Grupo acima da capacidade recomendada',
        message: `O grupo da reserva tem ${formatPeopleLabel(modalGuestCount)} e a composição final comporta ${formatPeopleLabel(recommendedCapacity)} com folga operacional.`,
        confirmLabel: 'Estou de acordo em seguir com a capacidade acima da recomendada.'
      };
    }

    return null;
  }, [modalCapacityEvaluationTables, modalGuestCount]);
  const mesaSelectValuesKey = mesaSelectValues.join('|');

  useEffect(() => {
    if (!selectedReservationId) return;
    const stillAvailable = availableReservationsForFloor.some(
      (res) => String(res.id) === String(selectedReservationId)
    );
    if (!stillAvailable) {
      setSelectedReservationId('');
    }
  }, [availableReservationsForFloor, selectedReservationId]);

  useEffect(() => {
    if (!selectedTable) {
      setSeatCount(1);
      return;
    }
    const min = Math.max(1, Math.floor(Number(selectedTable.minCovers) || 1));
    setSeatCount(min);
  }, [selectedTable]);
  useEffect(() => {
    setSeatCapacityWarningConfirmed(false);
  }, [
    seatCount,
    selectedReservationId,
    selectedTable?.id,
    selectedTableGuestCount,
    selectedTableIsAwaitingGuest
  ]);

  useEffect(() => {
    if (!showMesaSelectModal) return;

    const validTableIds = modalTableOptions.map((option) => String(option.table.id));

    const filteredValues = mesaSelectValues.filter((tableId) =>
      validTableIds.includes(String(tableId))
    );

    let nextValues = filteredValues;
    const hadInvalidSelection = filteredValues.length !== mesaSelectValues.length;

    if (hadInvalidSelection && filteredValues.length === 0) {
      if (mesaSelectModalIsLeaveMode) {
        const [firstOption] = modalTableOptions;
        nextValues = firstOption ? [String(firstOption.table.id)] : [];
      } else {
        const preferredOptions = modalTableOptions
          .filter((option) => option.isAwaitingForSelected || option.reservedForSelected)
          .map((option) => String(option.table.id));

        nextValues = preferredOptions.length > 0 ? preferredOptions : [];
      }
    }

    if (!areStringArraysEqual(nextValues, mesaSelectValues)) {
      setMesaSelectValues(nextValues);
    }
  }, [
    showMesaSelectModal,
    modalTableOptions,
    mesaSelectValues,
    mesaSelectModalIsLeaveMode,
  ]);
  useEffect(() => {
    setMesaCapacityOverflowConfirmed(false);
  }, [
    showMesaSelectModal?.reservaId,
    showMesaSelectModal?.mode,
    mesaSelectValuesKey
  ]);

  const handleSelectTable = (tableId: string) => {
    setSelectedTableId(String(tableId));
    setSelectedReservationId('');
    setActionError('');
  };
  const handleStartSelectedTableNameEdit = useCallback(() => {
    if (!selectedTable || selectedTableNameSaving) return;
    setSelectedTableNameDraft(selectedTable.name || '');
    setIsEditingSelectedTableName(true);
    setActionError('');
  }, [selectedTable, selectedTableNameSaving]);
  const handleCancelSelectedTableNameEdit = useCallback(() => {
    setIsEditingSelectedTableName(false);
    setSelectedTableNameDraft(selectedTable?.name || '');
  }, [selectedTable]);
  const handleSaveSelectedTableName = useCallback(async () => {
    if (!selectedTable || selectedTableNameSaving) return;

    const nextName = selectedTableNameDraft.trim();
    if (!nextName) {
      toast.error('Informe um nome para a mesa.');
      return;
    }

    if (nextName === String(selectedTable.name || '').trim()) {
      setIsEditingSelectedTableName(false);
      return;
    }

    setSelectedTableNameSaving(true);
    try {
      const updated = await updateTable(String(selectedTable.id), { name: nextName });
      if (!updated) {
        toast.error('Não foi possível atualizar o nome da mesa.');
        return;
      }

      toast.success('Nome da mesa atualizado.');
      setIsEditingSelectedTableName(false);
    } catch (error) {
      toast.error('Não foi possível atualizar o nome da mesa.');
    } finally {
      setSelectedTableNameSaving(false);
    }
  }, [selectedTable, selectedTableNameDraft, selectedTableNameSaving, updateTable]);

  const handleMesaSelectValueToggle = useCallback((tableId: string) => {
    const normalizedTableId = String(tableId);

    setMesaSelectValues((currentValues) => {
      if (mesaSelectModalIsLeaveMode) {
        return [normalizedTableId];
      }

      const nextValues = currentValues.includes(normalizedTableId)
        ? currentValues.filter((value) => value !== normalizedTableId)
        : [...currentValues, normalizedTableId];

      const nextValueSet = new Set(nextValues);
      return modalTableOptions
        .map((option) => String(option.table.id))
        .filter((value) => nextValueSet.has(value));
    });
  }, [mesaSelectModalIsLeaveMode, modalTableOptions]);
  const renderModalTableOption = useCallback((option: any) => {
    const tableId = String(option.table.id);
    const isSelected = mesaSelectValues.includes(tableId);

    return (
      <label
        key={option.table.id}
        className={`reservation-modal-table-option ${isSelected ? 'is-selected' : ''}`}
      >
        <input
          className="reservation-modal-table-input"
          type={mesaSelectModalAllowsMultiple ? 'checkbox' : 'radio'}
          name="reservation-modal-table"
          value={tableId}
          checked={isSelected}
          onChange={() => handleMesaSelectValueToggle(tableId)}
          disabled={!modalFloorData || mesaModalSubmitting}
        />
        <div className="reservation-modal-table-content">
          <span className="reservation-modal-table-name">{option.table.name}</span>
          <span className="reservation-modal-table-capacity">
            {formatTableCapacityRange(option.table)}
          </span>
          <div className="reservation-modal-table-meta">
            {!mesaSelectModalIsLeaveMode && option.reservedCount > 0 && (
              <span className="reservation-modal-table-badge">
                Reservada
              </span>
            )}
            {option.reservedForSelected && (
              <span className="reservation-modal-table-badge is-selected-reservation">
                Desta reserva
              </span>
            )}
            {option.isCurrentlySeated && (
              <span className="reservation-modal-table-badge is-seated">
                Em uso
              </span>
            )}
          </div>
        </div>
      </label>
    );
  }, [
    handleMesaSelectValueToggle,
    mesaModalSubmitting,
    mesaSelectModalAllowsMultiple,
    mesaSelectModalIsLeaveMode,
    mesaSelectValues,
    modalFloorData
  ]);

  const handleSelectReservation = (value: string) => {
    setSelectedReservationId(value);
    setActionError('');
    if (!value) return;

    const reservation = availableReservationsForFloor.find(
      (res) => String(res.id) === String(value)
    );
    if (!reservation) return;

    const guests = Math.max(1, Math.floor(Number(reservation.guests) || 1));
    setSeatCount(guests);
  };

  const persistReservationMesaIds = useCallback(async (
    reservationId?: string | number | null,
    mesaIds: Array<string | number> = [],
    extraData: Record<string, any> = {}
  ) => {
    if (!reservationId) return;
    const normalizedMesaIds = [...new Set(
      (Array.isArray(mesaIds) ? mesaIds : [])
        .map((mesaId) => String(mesaId))
        .filter(Boolean)
    )];

    await updateReservation(reservationId, {
      mesa_id: normalizedMesaIds[0] ?? null,
      mesa_ids: normalizedMesaIds,
      ...extraData,
    });
  }, [updateReservation]);

  const releaseMesaLinksForReservation = useCallback(async (
    reservationId?: string | number | null,
    options?: { onlyAwaiting?: boolean; mesaIds?: Array<string | number> }
  ) => {
    if (!reservationId) return false;

    const targetMesaIds = options?.mesaIds
      ? new Set(options.mesaIds.map((mesaId) => String(mesaId)))
      : null;

    const linkedOcupacoes = getReservationOcupacoes(reservationId, {
      awaitingOnly: options?.onlyAwaiting,
      seatedOnly: false,
    }).filter((ocupacao) => (
      !targetMesaIds || targetMesaIds.has(String(ocupacao?.mesa_id))
    ));

    if (linkedOcupacoes.length === 0) {
      return false;
    }

    await Promise.all(
      linkedOcupacoes.map((ocupacao) => liberarMesa(String(ocupacao.mesa_id), {
        data: selectedDateKey,
        reserva_id: reservationId,
      }))
    );
    return true;
  }, [getReservationOcupacoes, selectedDateKey]);

  const releaseAwaitingSelectionForReservation = useCallback((
    reservationId?: string | number | null,
    mesaIds?: Array<string | number>
  ) => (
    releaseMesaLinksForReservation(reservationId, { onlyAwaiting: true, mesaIds })
  ), [releaseMesaLinksForReservation]);

  const getReservationLinkedMesaIds = useCallback((
    reservationId?: string | number | null
  ) => {
    if (!reservationId) return [];

    const normalizedReservationId = String(reservationId);
    const reservationMesaIds = extractReservationTableIds(
      reservationById.get(normalizedReservationId) || null
    );
    const awaitingMesaIds = extractMesaIdsFromOcupacoes(
      getReservationOcupacoes(normalizedReservationId, { awaitingOnly: true })
    );
    const seatedMesaIds = extractMesaIdsFromOcupacoes(
      getReservationOcupacoes(normalizedReservationId, { seatedOnly: true })
    );

    return [...new Set([
      ...reservationMesaIds,
      ...awaitingMesaIds,
      ...seatedMesaIds,
    ])];
  }, [getReservationOcupacoes, reservationById]);

  const handleReleaseReservationTables = useCallback(async (
    reservationId?: string | number | null,
    mesaIdsToRelease: Array<string | number> = [],
    options?: { concludeWhenEmpty?: boolean; reservationUpdateData?: Record<string, any> }
  ) => {
    const normalizedReservationId = reservationId ? String(reservationId) : '';
    const normalizedMesaIds = [...new Set(
      mesaIdsToRelease.map((mesaId) => String(mesaId)).filter(Boolean)
    )];

    if (normalizedMesaIds.length === 0) return false;

    const currentAwaitingIds = extractMesaIdsFromOcupacoes(
      getReservationOcupacoes(normalizedReservationId, { awaitingOnly: true })
    );
    const currentSeatedIds = extractMesaIdsFromOcupacoes(
      getReservationOcupacoes(normalizedReservationId, { seatedOnly: true })
    );

    await releaseMesaLinksForReservation(normalizedReservationId, { mesaIds: normalizedMesaIds });

    const nextAwaitingIds = currentAwaitingIds.filter((mesaId) => !normalizedMesaIds.includes(mesaId));
    const nextSeatedIds = currentSeatedIds.filter((mesaId) => !normalizedMesaIds.includes(mesaId));
    const nextMesaIds = [...new Set([...nextSeatedIds, ...nextAwaitingIds])];

    const reservation = reservationById.get(normalizedReservationId) || null;
    const statusAtual = normalizeStatusValue(reservation?.status);
    const shouldConclude = Boolean(
      options?.concludeWhenEmpty !== false
      && normalizedMesaIds.some((mesaId) => currentSeatedIds.includes(mesaId))
      && nextMesaIds.length === 0
      && statusAtual !== 'concluida'
      && statusAtual !== 'cancelada'
      && statusAtual !== 'no_show'
    );

    await persistReservationMesaIds(normalizedReservationId, nextMesaIds, {
      ...(shouldConclude ? { status: 'concluida' } : {}),
      ...(options?.reservationUpdateData || {})
    });
    await loadMesaOcupacoes();
    return true;
  }, [
    getReservationOcupacoes,
    loadMesaOcupacoes,
    persistReservationMesaIds,
    releaseMesaLinksForReservation,
    reservationById
  ]);

  const handleSeatSelected = async () => {
    if (!selectedTable) return;
    const min = Math.max(1, Math.floor(Number(selectedTable.minCovers) || 1));
    const count = Math.floor(Number(seatCount));
    const reservaId = selectedReservationId ? String(selectedReservationId) : null;
    const shouldAllowCapacityOverflow = Boolean(seatCapacityWarning && seatCapacityWarningConfirmed);

    if (!Number.isFinite(count) || count <= 0) {
      setActionError(`Número de pessoas deve ser no mínimo ${min}.`);
      return;
    }

    if (seatCapacityWarning && !seatCapacityWarningConfirmed) {
      setActionError(`Confirme o aviso de capacidade para seguir com menos de ${min} pessoas.`);
      return;
    }

    setActionLoadingId(String(selectedTable.id));
    try {
      const payload: Record<string, any> = { numero_pessoas: count, data: selectedDateKey };
      if (reservaId) {
        payload.reserva_id = reservaId;
      }
      if (shouldAllowCapacityOverflow) {
        payload.capacidade_excedida_confirmada = true;
      }
      await ocuparMesa(String(selectedTable.id), payload);
      if (reservaId) {
        const currentMesaIds = getReservationLinkedMesaIds(reservaId);
        await persistReservationMesaIds(
          reservaId,
          [...currentMesaIds, String(selectedTable.id)],
          shouldAllowCapacityOverflow ? { capacidade_excedida_confirmada: true } : {}
        );
      }
      await maybeConfirmReservationOnSeat(reservaId);
      await loadMesaOcupacoes();
      setActionError('');
      setSelectedReservationId('');
    } catch (error) {
      setActionError('Não foi possível ocupar esta mesa.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleSeatAssignedSelected = async () => {
    if (!selectedTable || !selectedTableOcupacao) return;
    const reservaId = selectedTableOcupacao?.reserva_id ? String(selectedTableOcupacao.reserva_id) : null;
    const peopleCount = selectedTableGuestCount || selectedTableCapacity.min;
    const shouldAllowCapacityOverflow = Boolean(seatCapacityWarning && seatCapacityWarningConfirmed);

    if (seatCapacityWarning && !seatCapacityWarningConfirmed) {
      setActionError('Confirme o aviso de capacidade para continuar.');
      return;
    }

    setActionLoadingId(String(selectedTable.id));
    try {
      const payload: Record<string, any> = { numero_pessoas: peopleCount, data: selectedDateKey };
      if (reservaId) {
        payload.reserva_id = reservaId;
        await releaseAwaitingSelectionForReservation(reservaId, [String(selectedTable.id)]);
      }
      if (shouldAllowCapacityOverflow) {
        payload.capacidade_excedida_confirmada = true;
      }
      await ocuparMesa(String(selectedTable.id), payload);
      if (reservaId) {
        const currentMesaIds = getReservationLinkedMesaIds(reservaId);
        await persistReservationMesaIds(
          reservaId,
          [...currentMesaIds, String(selectedTable.id)],
          shouldAllowCapacityOverflow ? { capacidade_excedida_confirmada: true } : {}
        );
      }
      await maybeConfirmReservationOnSeat(reservaId);
      await loadMesaOcupacoes();
      setActionError('');
    } catch (error) {
      setActionError('Não foi possível sentar esta reserva.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleLeaveSelected = async (options?: { releaseAll?: boolean }) => {
    if (!selectedTable) return;
    const ocupacaoAtual = ocupacaoByMesaId.get(String(selectedTable.id));
    const reservaId = ocupacaoAtual?.reserva_id ? String(ocupacaoAtual.reserva_id) : '';
    setActionLoadingId(String(selectedTable.id));
    try {
      if (reservaId) {
        const mesaIdsToRelease = options?.releaseAll
          ? selectedTableReservationActiveTableIds
          : [String(selectedTable.id)];
        await handleReleaseReservationTables(reservaId, mesaIdsToRelease);
      } else {
        await liberarMesa(String(selectedTable.id), { data: selectedDateKey });
        await loadMesaOcupacoes();
      }
      setSelectedReservationId('');
      setActionError('');
    } catch (error) {
      setActionError('Não foi possível liberar esta mesa.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleStatusChange = async (reservationId: string | number, nextStatus: string) => {
    if (!reservationId) return false;
    setStatusUpdatingId(String(reservationId));
    try {
      await updateReservation(reservationId, { status: nextStatus });
      if (isUnavailableReservationStatus(nextStatus)) {
        await loadMesaOcupacoes();
      }
      toast.success('Status atualizado com sucesso.');
      return true;
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Não foi possível atualizar o status.');
      return false;
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleConfirmNoShow = async () => {
    if (!noShowConfirmation) return;
    const updated = await handleStatusChange(noShowConfirmation.reservationId, 'no_show');
    if (updated) {
      setNoShowConfirmation(null);
    }
  };
  const handleFinalizeReservationService = useCallback(async (
    reservationId?: string | number | null,
    mesaIdsToRelease: Array<string | number> = [],
  ) => {
    const normalizedReservationId = reservationId ? String(reservationId) : '';
    const normalizedMesaIds = [...new Set(
      mesaIdsToRelease.map((mesaId) => String(mesaId)).filter(Boolean)
    )];

    if (!normalizedReservationId || normalizedMesaIds.length === 0) return false;

    setStatusUpdatingId(normalizedReservationId);
    try {
      await handleReleaseReservationTables(normalizedReservationId, normalizedMesaIds);
      toast.success('Reserva finalizada com sucesso.');
      return true;
    } catch (error) {
      console.error('Erro ao finalizar reserva:', error);
      toast.error('Não foi possível finalizar a reserva.');
      return false;
    } finally {
      setStatusUpdatingId(null);
    }
  }, [handleReleaseReservationTables]);
  const maybeConfirmReservationOnSeat = useCallback(async (reservationId?: string | number | null) => {
    if (!reservationId) return;
    const reservation = reservations.find((res) => String(res.id) === String(reservationId));
    if (!reservation) return;
    const normalizedStatus = normalizeStatusValue(reservation.status);
    const effectiveStatus = normalizedStatus || (requiresRestaurantConfirmation ? 'pendente' : 'confirmada');
    if (effectiveStatus === 'cancelada' || effectiveStatus === 'no_show') return;
    if (effectiveStatus === 'confirmada') return;
    try {
      await updateReservation(reservationId, { status: 'confirmada' });
    } catch (error) {
      toast.error('Não foi possível confirmar a reserva.');
    }
  }, [requiresRestaurantConfirmation, reservations, updateReservation]);

  const handleRoomSeatSubmit = useCallback(async (seatRequest: RoomSeatModalState) => {
    if (!seatRequest?.sourceId || !seatRequest?.salaId) return;

    setRoomSeatSubmitting(true);
    if (seatRequest.source === 'reservation') {
      setStatusUpdatingId(seatRequest.sourceId);
    }

    try {
      if (seatRequest.source === 'reservation') {
        await ocuparSala(seatRequest.salaId, {
          reserva_id: seatRequest.sourceId,
          numero_pessoas: seatRequest.guests,
          nome_cliente: seatRequest.name,
          telefone_cliente: seatRequest.phone || null,
          data: selectedDateKey,
        });
        await maybeConfirmReservationOnSeat(seatRequest.sourceId);
      } else {
        await seatWaitlistEntry(seatRequest.sourceId, {
          sala_id: seatRequest.salaId,
          numero_pessoas: seatRequest.guests,
          data: selectedDateKey,
        });
        setWaitlistRefreshKey((current) => current + 1);
      }

      await loadMesaOcupacoes();
      await reloadReservations();
      setRoomSeatModal(null);
      toast.success(`${seatRequest.name || 'Cliente'} foi sentado(a).`);
    } catch (error) {
      const message = (error as any)?.response?.data?.error
        || (error as any)?.response?.data?.message
        || 'Não foi possível sentar este grupo no ambiente selecionado.';
      toast.error(message);
    } finally {
      setRoomSeatSubmitting(false);
      if (seatRequest.source === 'reservation') {
        setStatusUpdatingId(null);
      }
    }
  }, [loadMesaOcupacoes, maybeConfirmReservationOnSeat, reloadReservations, selectedDateKey]);

  const requestRoomSeat = useCallback((request: Omit<RoomSeatModalState, 'salaId'> & { salaId?: string }) => {
    const capacityFloors = floors.filter((floor) => !usesTableMap(floor, legacyMapDefault));
    const preferredRoomId = request.salaId ?? currentFloorData?.id ?? floors[0]?.id;
    const targetRoom = capacityFloors.find((floor) => String(floor.id) === String(preferredRoomId)) || capacityFloors[0];

    if (!targetRoom?.id) {
      toast.error('Cadastre um ambiente antes de sentar este grupo.');
      return;
    }

    const nextRequest: RoomSeatModalState = {
      ...request,
      salaId: String(targetRoom.id),
    };

    if (capacityFloors.length > 1) {
      setRoomSeatModal(nextRequest);
      return;
    }

    void handleRoomSeatSubmit(nextRequest);
  }, [currentFloorData?.id, floors, handleRoomSeatSubmit, legacyMapDefault]);

  const requestReservationRoomSeat = useCallback((reservation: any) => {
    if (!reservation?.id) return;
    requestRoomSeat({
      source: 'reservation',
      sourceId: String(reservation.id),
      name: reservation.name || `Reserva ${reservation.id}`,
      phone: reservation.phone || null,
      guests: Math.max(1, Math.floor(Number(reservation.guests) || 1)),
      salaId: reservation.roomId ? String(reservation.roomId) : undefined,
    });
  }, [requestRoomSeat]);

  const openSalaReleaseModal = useCallback((ocupacao: SalaOcupacao) => {
    const presentes = Math.max(1, Math.floor(Number(ocupacao.numero_pessoas_presentes) || 1));
    setSalaReleaseModal({
      ocupacao,
      quantidadeSaida: presentes,
    });
  }, []);

  const handleSalaReleaseSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!salaReleaseModal) return;

    const presentes = Math.max(
      1,
      Math.floor(Number(salaReleaseModal.ocupacao.numero_pessoas_presentes) || 1)
    );
    const quantidadeSaida = Math.floor(Number(salaReleaseModal.quantidadeSaida));
    if (!Number.isFinite(quantidadeSaida) || quantidadeSaida < 1 || quantidadeSaida > presentes) {
      toast.warning(`Informe uma quantidade entre 1 e ${presentes}.`);
      return;
    }

    setSalaReleaseSubmitting(true);
    try {
      await liberarSalaOcupacao(String(salaReleaseModal.ocupacao.id), quantidadeSaida);
      await loadMesaOcupacoes();
      await reloadReservations();
      setSalaReleaseModal(null);
      toast.success(
        quantidadeSaida === presentes
          ? 'Todas as cadeiras do grupo foram liberadas.'
          : `${formatPeopleLabel(quantidadeSaida)} liberada${quantidadeSaida !== 1 ? 's' : ''}.`
      );
    } catch (error) {
      const message = (error as any)?.response?.data?.error
        || (error as any)?.response?.data?.message
        || 'Não foi possível liberar as cadeiras.';
      toast.error(message);
    } finally {
      setSalaReleaseSubmitting(false);
    }
  }, [loadMesaOcupacoes, reloadReservations, salaReleaseModal]);

  const openMesaSelectModal = useCallback((reservation: any, mode: MesaSelectModalMode) => {
    if (!reservation?.id) return;

    const reservationId = String(reservation.id);
    const targetRoomId = reservation.roomId ?? currentFloorData?.id ?? floors[0]?.id;

    if (!targetRoomId) {
      toast.error('Não foi possível identificar o ambiente da reserva.');
      return;
    }

    const targetFloor = floorsById.get(String(targetRoomId));
    const currentAwaitingTableIds = extractMesaIdsFromOcupacoes(
      getReservationOcupacoes(reservationId, { awaitingOnly: true })
    );
    const currentSeatedTableIds = extractMesaIdsFromOcupacoes(
      getReservationOcupacoes(reservationId, { seatedOnly: true })
    );
    const linkedTableIds = extractReservationTableIds(reservation);
    const candidateTableIds = mode === 'leave'
      ? currentSeatedTableIds
      : [...new Set([
        ...currentAwaitingTableIds,
        ...linkedTableIds.filter((tableId) => !currentSeatedTableIds.includes(String(tableId)))
      ])];
    const nextSelectedTableIds = candidateTableIds.filter((tableId) => {
      const normalizedTableId = String(tableId);
      if (!targetFloor?.tables?.some((table: any) => String(table.id) === normalizedTableId)) {
        return false;
      }
      if (mode === 'leave') return true;
      return canUseTableForReservation(normalizedTableId, reservationId);
    });

    setActionError('');
    setMesaSelectValues(mode === 'leave' ? nextSelectedTableIds.slice(0, 1) : nextSelectedTableIds);
    setShowMesaSelectModal({
      reservaId: reservationId,
      guests: Math.max(1, Math.floor(Number(reservation.guests) || 1)),
      salaId: targetRoomId,
      mode,
    });
  }, [canUseTableForReservation, currentFloorData, floors, floorsById, getReservationOcupacoes]);

  const openWaitlistSeatModal = useCallback((entry: WaitlistEntry) => {
    if (!entry?.id) return;

    const preferredRoomId = entry.sala_id ?? currentFloorData?.id ?? floors[0]?.id;
    const fallbackRoomId = currentFloorData?.id ?? floors[0]?.id;
    const targetRoomId = preferredRoomId && floorsById.has(String(preferredRoomId))
      ? preferredRoomId
      : fallbackRoomId;
    if (!targetRoomId) {
      toast.error('Não foi possível identificar um ambiente para sentar este cliente.');
      return;
    }

    if (!usesTableMap(floorsById.get(String(targetRoomId)), legacyMapDefault)) {
      requestRoomSeat({
        source: 'waitlist',
        sourceId: String(entry.id),
        name: entry.nome_cliente || 'Cliente da fila',
        phone: entry.telefone_cliente || null,
        guests: Math.max(1, Math.floor(Number(entry.numero_pessoas) || 1)),
        salaId: String(targetRoomId),
      });
      return;
    }

    if (!floorsById.has(String(targetRoomId))) {
      toast.error('Ambiente da fila não encontrado no mapa de mesas.');
      return;
    }

    setActionError('');
    setMesaSelectValues([]);
    setShowMesaSelectModal({
      waitlistEntry: entry,
      guests: Math.max(1, Math.floor(Number(entry.numero_pessoas) || 1)),
      salaId: targetRoomId,
      mode: 'waitlist-seat',
    });
  }, [currentFloorData, floors, floorsById, requestRoomSeat, legacyMapDefault]);

  const handleWaitlistSeatRoomChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextSalaId = event.target.value;
    const entry = showMesaSelectModal?.waitlistEntry;
    if (entry && !usesTableMap(floorsById.get(String(nextSalaId)), legacyMapDefault)) {
      setShowMesaSelectModal(null);
      requestRoomSeat({
        source: 'waitlist',
        sourceId: String(entry.id),
        name: entry.nome_cliente || 'Cliente da fila',
        phone: entry.telefone_cliente || null,
        guests: Math.max(1, Math.floor(Number(entry.numero_pessoas) || 1)),
        salaId: nextSalaId,
      });
      return;
    }
    setActionError('');
    setMesaSelectValues([]);
    setMesaCapacityOverflowConfirmed(false);
    setShowMesaSelectModal((current) => {
      if (!current || current.mode !== 'waitlist-seat') return current;
      return {
        ...current,
        salaId: nextSalaId,
      };
    });
  }, [showMesaSelectModal, floorsById, legacyMapDefault, requestRoomSeat]);

  const handleMesaSelectModalSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!showMesaSelectModal) return;

    const { reservaId, guests, mode, waitlistEntry } = showMesaSelectModal;
    const selectedMesaIds = [...new Set(
      mesaSelectValues.map((mesaId) => String(mesaId)).filter(Boolean)
    )];
    const shouldAllowCapacityOverflow = Boolean(
      modalCapacityWarning
      && mesaCapacityOverflowConfirmed
    );

    if (mode === 'waitlist-seat') {
      if (!waitlistEntry?.id || selectedMesaIds.length === 0) return;

      setMesaModalSubmitting(true);
      try {
        await seatWaitlistEntry(String(waitlistEntry.id), {
          mesa_ids: selectedMesaIds,
          numero_pessoas: guests,
          data: selectedDateKey,
          ...(shouldAllowCapacityOverflow ? { capacidade_excedida_confirmada: true } : {}),
        });
        await loadMesaOcupacoes();
        setWaitlistRefreshKey((current) => current + 1);
        setShowMesaSelectModal(null);
        toast.success(`${waitlistEntry.nome_cliente || 'Cliente da fila'} foi sentado(a).`);
      } catch (error) {
        toast.error('Não foi possível sentar o cliente da fila nas mesas selecionadas.');
      } finally {
        setMesaModalSubmitting(false);
      }
      return;
    }

    if (!reservaId) return;

    const currentAwaitingTableIds = extractMesaIdsFromOcupacoes(
      getReservationOcupacoes(String(reservaId), { awaitingOnly: true })
    );
    const currentSeatedTableIds = extractMesaIdsFromOcupacoes(
      getReservationOcupacoes(String(reservaId), { seatedOnly: true })
    );
    const currentReservationMesaIds = getReservationLinkedMesaIds(reservaId);
    const mesasAwaitingToRelease = currentAwaitingTableIds.filter(
      (mesaId) => !selectedMesaIds.includes(mesaId)
    );
    const nextMesaIds = mode === 'seat'
      ? [...new Set([...currentReservationMesaIds, ...selectedMesaIds])]
      : [...new Set([...currentSeatedTableIds, ...selectedMesaIds])];
    const payload = {
      reserva_id: reservaId,
      numero_pessoas: guests,
      data: selectedDateKey,
      mesa_ids: nextMesaIds,
      ...(shouldAllowCapacityOverflow ? { capacidade_excedida_confirmada: true } : {}),
    };

    setMesaModalSubmitting(true);
    try {
      if (mode === 'leave') {
        if (selectedMesaIds.length === 0) return;
        await handleReleaseReservationTables(reservaId, [selectedMesaIds[0]], {
          reservationUpdateData: shouldAllowCapacityOverflow
            ? { capacidade_excedida_confirmada: true }
            : {}
        });
        setShowMesaSelectModal(null);
        return;
      }

      if (mode === 'select') {
        for (const mesaId of mesasAwaitingToRelease) {
          await releaseAwaitingSelectionForReservation(reservaId, [mesaId]);
        }

        const mesasToSelect = selectedMesaIds.filter(
          (mesaId) => !currentReservationMesaIds.includes(mesaId)
        );
        for (const mesaId of mesasToSelect) {
          await selecionarMesa(mesaId, payload);
        }
      } else {
        const mesasAwaitingToSeat = currentAwaitingTableIds.filter(
          (mesaId) => selectedMesaIds.includes(mesaId)
        );
        const mesasToSeat = selectedMesaIds.filter(
          (mesaId) => !currentSeatedTableIds.includes(mesaId) && !currentAwaitingTableIds.includes(mesaId)
        );

        for (const mesaId of mesasAwaitingToSeat) {
          await releaseAwaitingSelectionForReservation(reservaId, [mesaId]);
          await ocuparMesa(mesaId, payload);
        }

        for (const mesaId of mesasToSeat) {
          await ocuparMesa(mesaId, payload);
        }

        await maybeConfirmReservationOnSeat(reservaId);
      }

      await persistReservationMesaIds(
        reservaId,
        nextMesaIds,
        shouldAllowCapacityOverflow ? { capacidade_excedida_confirmada: true } : {}
      );
      await loadMesaOcupacoes();
      setShowMesaSelectModal(null);
    } catch (error) {
      toast.error(
        mode === 'seat'
          ? 'Não foi possível sentar a reserva nas mesas selecionadas.'
          : (
            mode === 'leave'
              ? 'Não foi possível levantar a mesa selecionada.'
              : 'Não foi possível selecionar as mesas para esta reserva.'
          )
      );
    } finally {
      setMesaModalSubmitting(false);
    }
  };

  const closeMesaSelectModal = useCallback(() => {
    if (mesaModalSubmitting) return;
    setShowMesaSelectModal(null);
    setMesaSelectValues([]);
    setMesaCapacityOverflowConfirmed(false);
  }, [mesaModalSubmitting]);
  const modalCanSubmit = useMemo(() => {
    if (!showMesaSelectModal || !modalFloorData || mesaModalSubmitting) return false;
    if (modalCapacityWarning && !mesaCapacityOverflowConfirmed) return false;
    if (mesaSelectModalIsLeaveMode || mesaSelectModalIsSeatMode || mesaSelectModalIsWaitlistSeatMode) {
      return mesaSelectValues.length > 0;
    }
    return (
      mesaSelectValues.length > 0
      || modalReservationLinkedTableIds.length > 0
      || modalReservationAwaitingTableIds.length > 0
    );
  }, [
    mesaCapacityOverflowConfirmed,
    mesaModalSubmitting,
    modalCapacityWarning,
    mesaSelectModalIsLeaveMode,
    mesaSelectModalIsSeatMode,
    mesaSelectModalIsWaitlistSeatMode,
    mesaSelectValues.length,
    modalFloorData,
    modalReservationAwaitingTableIds.length,
    modalReservationLinkedTableIds.length,
    showMesaSelectModal
  ]);

  const getTodayStatusPriority = useCallback((status?: string | null) => {
    const { displayStatus } = getReservationStatusMeta(status, requiresRestaurantConfirmation);
    return displayStatus === 'confirmada' || displayStatus === 'pendente' ? 0 : 1;
  }, [requiresRestaurantConfirmation]);

  const sortedTodayReservations = useMemo(() => {
    return [...filteredTodayReservations].sort((a, b) => {
      const statusCompare = getTodayStatusPriority(a.status) - getTodayStatusPriority(b.status);
      if (statusCompare !== 0) return statusCompare;

      const [ah, am] = a.time.split(':').map(Number);
      const [bh, bm] = b.time.split(':').map(Number);
      return ah !== bh ? ah - bh : am - bm;
    });
  }, [filteredTodayReservations, getTodayStatusPriority]);

  const finalizedTodayReservations = useMemo(() => {
    return sortedTodayReservations.filter((reservation) => isFinalizedReservationStatus(reservation.status));
  }, [sortedTodayReservations, isFinalizedReservationStatus]);

  const seatedTodayReservations = useMemo(() => {
    return sortedTodayReservations.filter((reservation) => seatedReservationIds.has(String(reservation.id)));
  }, [sortedTodayReservations, seatedReservationIds]);

  const upcomingTodayReservations = useMemo(() => {
    return sortedTodayReservations.filter((reservation) => (
      !isFinalizedReservationStatus(reservation.status)
      && (
        !seatedReservationIds.has(String(reservation.id))
        || awaitingReservationIds.has(String(reservation.id))
      )
    ));
  }, [sortedTodayReservations, seatedReservationIds, awaitingReservationIds, isFinalizedReservationStatus]);

  const todayReservationTabs = useMemo(() => ([
    {
      id: 'upcoming' as TodayReservationTab,
      label: 'Próximas Reservas',
      count: upcomingTodayReservations.length,
      emptyMessage: 'Nenhuma próxima reserva para este dia.',
    },
    {
      id: 'seated' as TodayReservationTab,
      label: 'Sentados',
      count: seatedTodayReservations.length,
      emptyMessage: 'Nenhuma reserva sentada neste dia.',
    },
    {
      id: 'finalized' as TodayReservationTab,
      label: 'Finalizadas',
      count: finalizedTodayReservations.length,
      emptyMessage: 'Nenhuma reserva concluída neste dia.',
    },
  ]), [
    finalizedTodayReservations.length,
    seatedTodayReservations.length,
    upcomingTodayReservations.length,
  ]);

  const activeTodayReservations = useMemo(() => {
    switch (todayReservationTab) {
      case 'finalized':
        return finalizedTodayReservations;
      case 'seated':
        return seatedTodayReservations;
      default:
        return upcomingTodayReservations;
    }
  }, [
    finalizedTodayReservations,
    seatedTodayReservations,
    todayReservationTab,
    upcomingTodayReservations,
  ]);

  const activeTodayTab = todayReservationTabs.find((tab) => tab.id === todayReservationTab) || todayReservationTabs[0];
  const todayReservationsEmptyMessage = requiresClientConfirmation && todayConfirmedOnly
    ? 'Nenhuma reserva confirmada para este filtro.'
    : (activeTodayTab?.emptyMessage || 'Nenhuma reserva para este dia.');

  const handleEditReservation = (reservationId: string | number) => {
    if (!reservationId) return;
    const reservation = reservations.find((res) => String(res.id) === String(reservationId));
    if (!reservation) return;
    setEditingReservation(reservation);
    setShowEditReservationModal(true);
  };

  const handleCloseEditReservation = () => {
    setShowEditReservationModal(false);
    setEditingReservation(null);
  };

  const handleReservationCancelledFromEdit = useCallback(async (reservationId: string | number) => {
    await releaseMesaLinksForReservation(reservationId);
    await loadMesaOcupacoes();
    if (String(selectedReservationId || '') === String(reservationId)) {
      setSelectedReservationId('');
    }
  }, [loadMesaOcupacoes, releaseMesaLinksForReservation, selectedReservationId]);

  const editingReservationDate = editingReservation?.data
    ? new Date(`${editingReservation.data}T00:00:00`)
    : selectedDate;
  const currentFloorStats = currentFloorData
    ? (
      floorStats.get(String(currentFloorData.id))
      || {
        total: usarMapaMesas
          ? (currentFloorData.tables?.length || 0)
          : Math.max(0, Number(currentFloorData.capacidade_maxima) || 0),
        occupied: 0,
        available: 0,
      }
    )
    : { total: 0, occupied: 0, available: 0 };

  return (
    <div className="admin-dashboard">
      <section className="dashboard-date-navigation" aria-label="Selecionar dia do dashboard">
        <button
          type="button"
          className="dashboard-date-navigation-button"
          onClick={() => setSelectedDate((currentDate) => shiftDate(currentDate, -1))}
          aria-label="Voltar um dia"
        >
          <ChevronLeft size={20} />
        </button>
        <h2 className="dashboard-date-navigation-title">{selectedDateLabel}</h2>
        <button
          type="button"
          className="dashboard-date-navigation-button"
          onClick={() => setSelectedDate((currentDate) => shiftDate(currentDate, 1))}
          aria-label="Avançar um dia"
        >
          <ChevronRight size={20} />
        </button>
      </section>

      {floors.length > 0 && (
        <DashboardSummaryPanel
          usarMapaMesas={usarMapaMesas}
          dashboardSummary={dashboardSummary}
          environmentName={currentFloorData?.name}
          dashboardWaitingCount={dashboardWaitingCount}
          reservationsFeatureEnabled={reservationsFeatureEnabled}
          waitlistFeatureEnabled={waitlistFeatureEnabled}
          reservationLink={reservationLink}
          waitlistLink={waitlistLink}
          restaurantSlug={restaurantSlug}
          handleOpenQrCodeModal={handleOpenQrCodeModal}
          handleCopyReservationLink={handleCopyReservationLink}
          handleCopyWaitlistLink={handleCopyWaitlistLink}
        />
      )}

      {floors.length > 0 && (
        <RoomFloorTabs
          floors={floors}
          currentFloor={currentFloor}
          setCurrentFloor={setCurrentFloor}
          floorStats={floorStats}
          usarMapaMesas={legacyMapDefault}
        />
      )}

      <section className="room-overview-panel">
        <div className="room-overview-header">
          <div className="room-overview-status">
            {occupancyLoading && <span className="room-loading">Atualizando ocupação...</span>}
            {occupancyError && <span className="room-error">{occupancyError}</span>}
          </div>
        </div>

        {floors.length === 0 && (
          <div className="room-empty room-empty--setup">
            <strong>Comece criando o primeiro ambiente do restaurante.</strong>
            <span>
              {usarMapaMesas
                ? 'Abra Ambientes para montar o salão, cadastrar mesas e liberar as reservas.'
                : 'Abra Ambientes para informar a capacidade e liberar as reservas.'}
            </span>
            <button
              type="button"
              className="room-empty-action"
              onClick={handleCreateInitialFloor}
              disabled={initialFloorCreating}
            >
              {initialFloorCreating ? 'Criando...' : 'Criar ambiente'}
            </button>
          </div>
        )}

        {!usarMapaMesas && currentFloorData && (
          <RoomCapacityOverview
            currentFloorData={currentFloorData}
            currentFloorStats={currentFloorStats}
            currentFloorSalaOcupacoes={currentFloorSalaOcupacoes}
            reservationById={reservationById}
            openSalaReleaseModal={openSalaReleaseModal}
            onConfigureCapacity={() => navigate('/reservo/plantas')}
          />
        )}

        {usarMapaMesas && floors.length > 0 && (
          <>
            {currentFloorData && (
              <>
                <div className="room-view">
                  <div className="room-view-main">
                    {currentFloorHasLayout ? (
                      <RoomLayoutMap
                        floor={currentFloorData}
                        occupiedTableIds={occupiedTableIds}
                        waitingTableIds={waitingTableIds}
                        reservedTableMeta={reservedTableMetaForFloor}
                        tableTooltipsById={tableTooltipById}
                        selectedTableId={selectedTableId}
                        onSelectTable={handleSelectTable}
                      />
                    ) : (
                      <RoomTableMap
                        floor={currentFloorData}
                        occupiedTableIds={occupiedTableIds}
                        waitingTableIds={waitingTableIds}
                        reservedTableMeta={reservedTableMetaForFloor}
                        tableTooltipsById={tableTooltipById}
                        selectedTableId={selectedTableId}
                        onSelectTable={handleSelectTable}
                      />
                    )}
                  </div>

                  <RoomSeatPanel
                    selectedTable={selectedTable}
                    isEditingSelectedTableName={isEditingSelectedTableName}
                    selectedTableNameDraft={selectedTableNameDraft}
                    setSelectedTableNameDraft={setSelectedTableNameDraft}
                    selectedTableNameInputRef={selectedTableNameInputRef}
                    selectedTableNameSaving={selectedTableNameSaving}
                    onSaveSelectedTableName={handleSaveSelectedTableName}
                    onCancelSelectedTableNameEdit={handleCancelSelectedTableNameEdit}
                    onStartSelectedTableNameEdit={handleStartSelectedTableNameEdit}
                    selectedTableStatusMeta={selectedTableStatusMeta}
                    selectedTableCapacity={selectedTableCapacity}
                    selectedTableOcupacao={selectedTableOcupacao}
                    selectedTableIsAwaitingGuest={selectedTableIsAwaitingGuest}
                    selectedTableGuestCount={selectedTableGuestCount}
                    selectedTableReservation={selectedTableReservation}
                    selectedTableReservationActiveTableNames={selectedTableReservationActiveTableNames}
                    selectedReservation={selectedReservation}
                    selectedTablePendingReservations={selectedTablePendingReservations}
                    actionError={actionError}
                    seatCapacityWarning={seatCapacityWarning}
                    seatCapacityWarningConfirmed={seatCapacityWarningConfirmed}
                    setSeatCapacityWarningConfirmed={setSeatCapacityWarningConfirmed}
                    actionLoadingId={actionLoadingId}
                    onSeatAssignedSelected={handleSeatAssignedSelected}
                    onLeaveSelected={handleLeaveSelected}
                    selectedTableCanLeaveAll={selectedTableCanLeaveAll}
                    selectedTableLeaveAllLabel={selectedTableLeaveAllLabel}
                    selectedReservationId={selectedReservationId || ''}
                    onSelectReservation={handleSelectReservation}
                    availableReservationsForFloor={availableReservationsForFloor}
                    seatedReservationIds={seatedReservationIds}
                    seatCount={seatCount}
                    setSeatCount={setSeatCount}
                    onSeatSelected={handleSeatSelected}
                  />
                </div>
              </>
            )}
          </>
        )}
      </section>

      {floors.length > 0 && (
        <>
            <DashboardTodayBoard
              effectiveBoardFilter={effectiveBoardFilter}
              reservationsFeatureEnabled={reservationsFeatureEnabled}
              waitlistFeatureEnabled={waitlistFeatureEnabled}
              dashboardSummary={dashboardSummary}
              waitlistCount={waitlistCount}
              setBoardFilter={setBoardFilter}
              requiresClientConfirmation={requiresClientConfirmation}
              todayConfirmedOnly={todayConfirmedOnly}
              setTodayConfirmedOnly={setTodayConfirmedOnly}
              todayRoomFilter={todayRoomFilter}
              setTodayRoomFilter={setTodayRoomFilter}
              floors={floors}
              setShowReservationModal={setShowReservationModal}
              selectedDateLabel={selectedDateLabel}
              todayReservationTabs={todayReservationTabs}
              todayReservationTab={todayReservationTab}
              setTodayReservationTab={setTodayReservationTab}
              activeTodayTab={activeTodayTab}
              activeTodayReservations={activeTodayReservations}
              todayReservationsEmptyMessage={todayReservationsEmptyMessage}
              awaitingOcupacoesByReservationId={awaitingOcupacoesByReservationId}
              seatedOcupacoesByReservationId={seatedOcupacoesByReservationId}
              salaOcupacaoByReservationId={salaOcupacaoByReservationId}
              usarMapaMesas={legacyMapDefault}
              resolveTableDisplayNames={resolveTableDisplayNames}
              getReservationStatusMeta={getReservationStatusMeta}
              requiresRestaurantConfirmation={requiresRestaurantConfirmation}
              isReservationCustomerConfirmed={isReservationCustomerConfirmed}
              isUnavailableReservationStatus={isUnavailableReservationStatus}
              handleEditReservation={handleEditReservation}
              STATUS_LABELS={STATUS_LABELS}
              statusUpdatingId={statusUpdatingId}
              openMesaSelectModal={openMesaSelectModal}
              openSalaReleaseModal={openSalaReleaseModal}
              requestReservationRoomSeat={requestReservationRoomSeat}
              handleReleaseReservationTables={handleReleaseReservationTables}
              handleFinalizeReservationService={handleFinalizeReservationService}
              setNoShowConfirmation={setNoShowConfirmation}
              selectedDateKey={selectedDateKey}
              setWaitlistCount={setWaitlistCount}
              openWaitlistSeatModal={openWaitlistSeatModal}
              waitlistRefreshKey={waitlistRefreshKey}
            />

            <DashboardModals
              qrCodeModal={qrCodeModal}
              activeQrCodeDataUrl={activeQrCodeDataUrl}
              setQrCodeModal={setQrCodeModal}
              handleCopyQrCode={handleCopyQrCode}
              handleSaveQrCode={handleSaveQrCode}
              showMesaSelectModal={showMesaSelectModal}
              closeMesaSelectModal={closeMesaSelectModal}
              mesaModalSubmitting={mesaModalSubmitting}
              mesaSelectModalIsLeaveMode={mesaSelectModalIsLeaveMode}
              mesaSelectModalIsSeatMode={mesaSelectModalIsSeatMode}
              mesaSelectModalIsWaitlistSeatMode={mesaSelectModalIsWaitlistSeatMode}
              mesaSelectModalAllowsMultiple={mesaSelectModalAllowsMultiple}
              handleMesaSelectModalSubmit={handleMesaSelectModalSubmit}
              modalFloorData={modalFloorData}
              floors={floors}
              legacyMapDefault={legacyMapDefault}
              handleWaitlistSeatRoomChange={handleWaitlistSeatRoomChange}
              modalSelectionSummary={modalSelectionSummary}
              modalGuestCount={modalGuestCount}
              modalWaitlistEntry={modalWaitlistEntry}
              modalSelectedCapacitySummary={modalSelectedCapacitySummary}
              modalCapacityWarning={modalCapacityWarning}
              mesaCapacityOverflowConfirmed={mesaCapacityOverflowConfirmed}
              setMesaCapacityOverflowConfirmed={setMesaCapacityOverflowConfirmed}
              modalTableGroups={modalTableGroups}
              modalTableOptions={modalTableOptions}
              renderModalTableOption={renderModalTableOption}
              modalReservation={modalReservation}
              resolveTableDisplayNames={resolveTableDisplayNames}
              modalCanSubmit={modalCanSubmit}
              roomSeatModal={roomSeatModal}
              setRoomSeatModal={setRoomSeatModal}
              roomSeatSubmitting={roomSeatSubmitting}
              handleRoomSeatSubmit={handleRoomSeatSubmit}
              floorStats={floorStats}
              salaReleaseModal={salaReleaseModal}
              setSalaReleaseModal={setSalaReleaseModal}
              salaReleaseSubmitting={salaReleaseSubmitting}
              handleSalaReleaseSubmit={handleSalaReleaseSubmit}
              reservationById={reservationById}
              noShowConfirmation={noShowConfirmation}
              setNoShowConfirmation={setNoShowConfirmation}
              statusUpdatingId={statusUpdatingId}
              handleConfirmNoShow={handleConfirmNoShow}
            />
            {showReservationModal && (
              <ReservationForm
                onClose={() => setShowReservationModal(false)}
                selectedDate={selectedDate}
                preselectedSalaId={currentFloorData?.id}
              />
            )}
            {showEditReservationModal && editingReservation && (
              <ReservationForm
                reservation={editingReservation}
                selectedDate={editingReservationDate}
                onClose={handleCloseEditReservation}
                onReservationCancelled={handleReservationCancelledFromEdit}
              />
            )}
        </>
      )}

    </div>
  );
};

export default AdminDashboard;
